// Cache for loaded animations to avoid reloading
const animationCache = new Map<string, any>();

// Utility function to load animations with error handling, timeout, and caching
export const loadAnimation = async (path: string, retries: number = 2): Promise<any> => {
  // Check cache first
  if (animationCache.has(path)) {
    return animationCache.get(path);
  }

  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      // Create AbortController for timeout (reduced timeout for better UX)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
      
      const response = await fetch(path, {
        signal: controller.signal,
        headers: {
          'Cache-Control': 'max-age=3600', // Cache for 1 hour
          'Accept': 'application/json'
        }
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const animationData = await response.json();
      
      // Cache the animation data
      animationCache.set(path, animationData);
      
      return animationData;
    } catch (error) {
      console.error(`Animation load attempt ${attempt} failed for ${path}:`, error);
      
      if (attempt === retries) {
        console.error(`All attempts failed for ${path}, returning null`);
        return null;
      }
      
      // Wait before retry (reduced delay)
      await new Promise(resolve => setTimeout(resolve, 500 * attempt));
    }
  }
  
  return null;
};
